import {inject, Injectable, Injector} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {TranslocoService} from '@jsverse/transloco';
import {CognitoIdToken, CognitoUser} from 'amazon-cognito-identity-js';
import {environment} from '../../environments/environment';
import {
  ErrorServerStatuses, OkHttpStatusCodes,
  ServerError,
  ServerResponse,
  ServerStatus
} from '../models/types.model';
import {buildParamString, IRequestParams} from '../helpers/params.helper';
import {UserService} from './user.service';
import {AuthService, userPool} from './auth.service';
import {ToastService} from './toast.service';
import {lastValueFrom} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  url = '';
  // domain = '';
  protected userService = inject(UserService);
  protected router = inject(Router);
  protected translate = inject(TranslocoService);
  private httpClient = inject(HttpClient);
  // AuthService extends this class: it is looked up when needed, not injected at construction.
  private injector = inject(Injector);

  constructor() {
    this.use(environment.api_url);
  }

  async getOut(message: string) {
    this.userService.logout();
    await this.router.navigateByUrl('/auth/sign-in');
  }

  assertOK(httpResponse: HttpResponse<unknown>): ServerResponse {
    const serverResponse = this.getServerResponse(httpResponse);

    if (ErrorServerStatuses.includes(serverResponse.status)) {
      throw new ServerError(
        serverResponse.message || serverResponse.status,
        serverResponse.statusCode || serverResponse.code || 500,
        serverResponse
      );
    }
    return serverResponse;
  }

  async executor(call: () => Promise<HttpResponse<unknown>>): Promise<ServerResponse> {
    try {
      const response = await call();
      return this.assertOK(response);
    } catch (err: any) { // eslint-disable-line
      let error = err;
      if (error.status == 401) {
        // authentication failed -> get new access token
        try {
          // try to ask new accessToken and re-execute the call (headers will be reconstructed)
          await this.getNewAccessToken();
          const response = await call();
          return this.assertOK(response);

        } catch (err: any) { // eslint-disable-line
          // if the second call fails -> fail
          if (err.status === 401) {
            // either again: authentication failed -> logout in the UX too
            await this.getOut('HttpService.executor (2) -> message: 401 - ' + err.message);
            return {status: ServerStatus.error, message: 'Session expired or wrong credentials'};

          } else if (this.isAppAccessDenied(err)) {
            // the refreshed token carries a user type without App access
            await this.refuseAppAccess();
            error = err;

          } else if (!this.userService.isLoggedIn()) {
            // a parallel call closed the session during the refresh and already took the user out
            error = err;

          } else {
            // normal call failure after second try
            await this.getOut('HttpService.executor (2) -> message: ' + err.status + ' - ' + err.message);
            error = err;
          }
        }
      } else if (this.isAppAccessDenied(error)) {
        // the App API refuses this user type (DownloadOnly): not an expired session, no token refresh
        await this.refuseAppAccess();
      } else {
        // normal call failure after 1st try
        console.log('HttpService.executor (1) -> message: ' + error.status + ' - ' + error.message);
      }
      if (error instanceof HttpErrorResponse) {
        return this.getServerResponse(error);
      } else if (error instanceof ServerError && error.response) {
        return error.response;
      } else {
        return {
          status: ServerStatus.error,
          statusCode: error.status,
          code: error.status,
          message: error.statusText || error.message
        };
      }
    }
  }

  private isAppAccessDenied(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 403 && error.error?.code === 'appAccessDenied';
  }

  // Same exit as isSignedInGuard for a user without App access.
  private async refuseAppAccess() {
    // Parallel calls are all refused: warn and log out once.
    if (!this.userService.isLoggedIn()) {
      return;
    }
    this.injector.get(ToastService).showWarning('auth.noAppAccess');
    await this.injector.get(AuthService).logout();
    await this.router.navigateByUrl('/auth/login');
  }

  async getNewAccessToken(): Promise<CognitoIdToken> {
    const refreshToken = this.userService.getRefreshToken();
    const cognitoUser = new CognitoUser({Username: this.userService.getUserData()?.email || '', Pool: userPool});

    return new Promise((resolve, reject) => {
      cognitoUser.refreshSession(refreshToken!, (error, session) => {
        if (error) {
          reject(error);
        } else if (!this.userService.getRefreshToken()) {
          // the session was closed meanwhile (e.g. App access refused to a parallel call): do not restore it
          reject(new Error('Session closed during the token refresh'));
        } else {
          // call "storeTokens", not "login", we don't want to alert the rest of the application
          this.userService.storeTokens(session.idToken, session.accessToken, session.refreshToken);
          resolve(session.idToken);
        }
      });
    });
  }

  use(url: string) {
    // remove trailing "/"
    this.url = (url[url.length - 1] === '/') ? url.slice(0, -1) : url;
  }

  // Http calls
  headers(contentType?: string, noAuth?: boolean) {
    const header: { [name: string]: string } = {'Cache-Control': 'no-cache, no-store'};
    if (contentType) {
      header['Content-Type'] = contentType;
    }

    if (noAuth) return header;
    const token = this.userService.getIdToken();
    if (token) {
      header['Authorization'] = 'Bearer ' + token.getJwtToken();
    }
    return header;
  }

  async post(path: string, body?: string | object, contentType?: string, noAuth: boolean = false): Promise<ServerResponse | null> {
    return this.executor(
      () => lastValueFrom(this.httpClient.post(this.url + path, body,
        {responseType: 'json', observe: 'response', headers: this.headers(contentType, noAuth)}
      ))
    );
  }

  async get(path: string, params?: string | IRequestParams): Promise<any> {
    if (typeof params === "string") {
      path += '?' + params;
    } else if (typeof params != "undefined") {
      path += this.getRequestParamString(params)
    }

    return this.executor(
      () => lastValueFrom(this.httpClient.get(this.url + path,
        {responseType: 'json', observe: 'response', headers: this.headers()}
      ))
    );
  }

  async patch(path: string, body?: string | object, contentType?: string): Promise<ServerResponse> {
    return this.executor(
      () => lastValueFrom(this.httpClient.patch(this.url + path, body,
        {responseType: 'json', observe: 'response', headers: this.headers(contentType)}
      ))
    );
  }

  async put(path: string, body?: string | object, contentType?: string, noAuth: boolean = false): Promise<ServerResponse> {
    return this.executor(
      () => lastValueFrom(this.httpClient.put(this.url + path, body,
        {responseType: 'json', observe: 'response', headers: this.headers(contentType, noAuth)}
      ))
    );
  }

  async delete(path: string, params?: string | IRequestParams): Promise<ServerResponse> {
    if (typeof params === "string") {
      path += '?' + params;
    } else if (typeof params != "undefined") {
      path += this.getRequestParamString(params)
    }

    return this.executor(
      () => lastValueFrom(this.httpClient.delete(this.url + path,
        {responseType: 'json', observe: 'response', headers: this.headers()}
      ))
    );
  }

  private getServerResponse(httpResponse: HttpResponse<unknown> | HttpErrorResponse): ServerResponse {
    const headers: Record<string, string> = {};
    for (const key of httpResponse.headers.keys()) {
      headers[key] = httpResponse.headers.get(key) || '';
    }

    const body = ('body' in httpResponse) ? httpResponse.body : httpResponse.error;
    const serverStatus = OkHttpStatusCodes.includes(httpResponse.status) ? ServerStatus.ok : ServerStatus.error;
    const message = typeof body === 'string' ? body : '';
    return {
      status: serverStatus, message: message || httpResponse.statusText,
      headers, code: httpResponse.status, statusCode: httpResponse.status,
      body
    };
  }

  // Helpers

  /**
   * @param requestParams {param: value, param2: value2}
   * @returns ?param=value&param2=value2
   */
  private getRequestParamString(requestParams: IRequestParams): string {
    return buildParamString(requestParams);
  }

}
